import { useState } from 'react';
import { Map as MapIcon } from 'lucide-react';
import { Switch } from '../../../../components/ui/switch';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { isSafeStyleUrl } from '../../../workspace/spatial/map/spatialMapView';
import { layerColorAt } from '../../../workspace/spatial/map/mapThemes';
import { SpatialMapHost } from '../../../workspace/spatial/map/SpatialMapLazy';
import type { SpatialMapLayerInput } from '../../../workspace/spatial/map/spatialMapTypes';
import type { DecodedGeometry } from '../../../workspace/spatial/decode/types';

function demoPoint(lng: number, lat: number): DecodedGeometry {
  return {
    geojson: { type: 'Point', coordinates: [lng, lat] },
    srid: 4326,
    geomType: 'Point',
    pointCount: 1,
    dimensions: 'XY',
  };
}

// A small seeded layer so the preview always shows geometry (even with no basemap / planar).
const PREVIEW_LAYERS: SpatialMapLayerInput[] = [
  {
    def: {
      id: 'preview',
      label: 'Preview',
      visible: true,
      source: { kind: 'query', sql: 'preview', geometryRef: { kind: 'column', column: 'geom' } },
      style: { color: layerColorAt(0) },
      featureCap: 100,
    },
    features: [
      { id: 1, geometry: demoPoint(-73.98, 40.75) },
      { id: 2, geometry: demoPoint(-0.12, 51.5) },
      { id: 3, geometry: demoPoint(2.35, 48.86) },
    ],
  },
];

export function SpatialSettingsTab() {
  const settings = useAppStore((s) => s.spatialSettings);
  const setSpatialSettings = useAppStore((s) => s.setSpatialSettings);
  const { track } = useAnalytics();
  const [urlDraft, setUrlDraft] = useState(settings.customStyleUrl ?? '');

  const invalidUrl = urlDraft !== '' && !isSafeStyleUrl(urlDraft);

  const onToggleBasemap = (enabled: boolean) => {
    setSpatialSettings({ basemapEnabled: enabled });
    // Fire on BOTH on and off. Never include the style URL (it can carry an API key).
    track('feature:spatial_basemap_toggle', { enabled });
  };

  const onChangeUrl = (value: string) => {
    setUrlDraft(value);
    if (value === '') {
      setSpatialSettings({ customStyleUrl: null });
      return;
    }
    // Only a validated http(s) URL is ever stored; an invalid one shows an inline error and is
    // never persisted (A18/NG3).
    if (isSafeStyleUrl(value)) setSpatialSettings({ customStyleUrl: value });
  };

  return (
    <div className="space-y-4" data-testid="spatial-settings-tab">
      <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
        <div className="flex items-center gap-3">
          <MapIcon className="h-5 w-5 text-muted-foreground" />
          <div className="grid gap-0.5">
            <Label htmlFor="spatial-basemap" className="cursor-pointer">
              Show basemap
            </Label>
            <div className="text-xs text-muted-foreground">
              Fetch map tiles from openfreemap.org to draw geography under your data. Turn off for a
              blank, offline planar grid.
            </div>
          </div>
        </div>
        <Switch
          id="spatial-basemap"
          checked={settings.basemapEnabled}
          onCheckedChange={onToggleBasemap}
          data-testid="spatial-basemap-toggle"
        />
      </div>

      <div className="space-y-2 rounded-md border border-border bg-background p-4">
        <Label htmlFor="spatial-custom-style" className="cursor-default">
          Custom map style URL
        </Label>
        <div className="text-xs text-muted-foreground">
          Optional. An https link to a MapLibre style JSON (e.g. your own tile provider). Leave blank
          to use the built-in basemap.
        </div>
        <Input
          id="spatial-custom-style"
          value={urlDraft}
          onChange={(e) => onChangeUrl(e.target.value)}
          placeholder="https://example.com/style.json"
          spellCheck={false}
          autoComplete="off"
          data-testid="spatial-custom-style-input"
          aria-invalid={invalidUrl}
        />
        {invalidUrl && (
          <div className="text-xs text-destructive" data-testid="spatial-custom-style-error">
            Enter a valid http(s) URL without embedded credentials.
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium">Preview</div>
        <div className="h-48 w-full overflow-hidden rounded-md border border-border">
          <SpatialMapHost layers={PREVIEW_LAYERS} embedded fitToData />
        </div>
      </div>
    </div>
  );
}
